/**
 * Source status coercion and edge-color utilities.
 *
 * Canonical values: "VERIFIED", "UNVERIFIED", or null (unset).
 * The amaryllis upload format (feature 024) does not carry a source_status
 * column, so "unset" is a first-class state everywhere this module is used.
 * Coercion handles misspellings, hyphens, and whitespace variations on any
 * values that do appear (legacy edges, manual edits).
 */
import type { Theme } from '@fti/design-system';

export type SourceStatus = 'VERIFIED' | 'UNVERIFIED';

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

/**
 * Coerce a raw source_status value to canonical UPPERCASE form, or null.
 *
 * Returns null for any empty/missing/unrecognized input so callers can
 * treat "unset" as a first-class state.
 */
export function coerceSourceStatus(value: unknown): SourceStatus | null {
  if (value == null) return null;
  const str = typeof value === 'string' ? value : String(value);
  const trimmed = str.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/[-\s]+/g, '').toLowerCase();
  if (!normalized) return null;

  if (normalized === 'verified' || normalized === 'verify') return 'VERIFIED';
  if (normalized === 'unverified' || normalized === 'unverify') return 'UNVERIFIED';

  if (normalized.startsWith('unverif')) return 'UNVERIFIED';
  if (normalized.startsWith('verif')) return 'VERIFIED';

  if (levenshtein(normalized, 'verified') <= 2) return 'VERIFIED';
  if (levenshtein(normalized, 'unverified') <= 2) return 'UNVERIFIED';

  return null;
}

/** True if the raw value coerces to a known source status. */
export function isSourceStatusSet(value: unknown): boolean {
  return coerceSourceStatus(value) !== null;
}

/**
 * Human-readable label for a source_status value.
 * Returns null when unset so callers can choose to omit the row entirely.
 */
export function sourceStatusLabel(value: unknown): string | null {
  const coerced = coerceSourceStatus(value);
  if (coerced === 'VERIFIED') return 'Verified';
  if (coerced === 'UNVERIFIED') return 'Unverified';
  return null;
}

/**
 * Edge color for a given source_status. Unset values fall through to the
 * theme's default edge color.
 */
export function getEdgeColorForStatus(
  status: unknown,
  theme: Theme,
): string {
  const coerced = coerceSourceStatus(status);
  if (coerced === 'VERIFIED') return theme.colors.semantic.success.default;
  if (coerced === 'UNVERIFIED') return theme.colors.semantic.error.default;
  return theme.colors.border.default;
}
